import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from "@mui/material"
import { useReactToPrint } from "react-to-print"
import { useRef } from "react"
import { Print as PrintIcon } from "@mui/icons-material"
import { useParams } from "react-router-dom"
import { useGetInvoiceQuery } from "src/api/modules/invoice-api"
import { PageLoader } from "src/shared/components"

const InvoicePreview = () => {
  const componentRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const { data: respInvoice, isLoading, isFetching } = useGetInvoiceQuery({ id: id as string }, { skip: !id })
  const invoiceData = respInvoice?.data
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Invoice",
    onAfterPrint: () => console.log("Print completed")
  })

  return (
    <>
      <PageLoader isLoading={isLoading || isFetching} />
      <style>
        {`
          @media print {
            @page {
              margin: 0.5in;
              size: A4;
            }
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .no-print {
              display: none !important;
            }
            .print-container {
              display: block !important;
              margin: 0 !important;
              padding: 0 !important;
              text-align: left !important;
            }
            .print-content {
              box-shadow: none !important;
              border: none !important;
              margin: 0 !important;
              padding: 20px !important;
            }
            .print-table {
              page-break-inside: avoid;
            }
            .print-section {
              page-break-inside: avoid;
              margin-bottom: 20px;
            }
          }
        `}
      </style>

      {/* Main Container */}
      <Box
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        bgcolor="#f5f6fa"
        minHeight="100vh"
        className="print-container"
      >
        {/* Print Button */}
        <Box mb={3} display="flex" justifyContent="center" className="no-print">
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0"
              }
            }}
          >
            Print Invoice
          </Button>
        </Box>

        {/* Printable Invoice Content */}
        <Box
          ref={componentRef}
          sx={{
            width: "100%",
            maxWidth: 900
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              backgroundColor: "white"
            }}
            className="print-content"
          >
            {/* Invoice Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} className="print-section">
              {/* <Typography variant="h6" fontWeight={700}>
                Your Logo Here
              </Typography> */}
              <Typography variant="h4" fontWeight={700}>
                INVOICE
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Invoice Information */}
            <Grid container spacing={2} mb={2} className="print-section">
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2">Bill To</Typography>
                <Typography variant="body2">{invoiceData?.clientName}</Typography>
                {/* <Typography variant="body2">City, Country, Postal</Typography> */}
              </Grid>
              <Grid size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                <Typography variant="body2">
                  INVOICE NUMBER: <b>{invoiceData?.secondaryId}</b>
                </Typography>
                <Typography variant="body2">
                  INVOICE DATE: <b>{invoiceData?.invoiceDate}</b>
                </Typography>
                <Typography variant="body2">
                  DUE DATE: <b>{invoiceData?.dueDate}</b>
                </Typography>
                <Typography variant="body2">
                  STATUS: <b>{invoiceData?.status}</b>
                </Typography>
              </Grid>
            </Grid>
            {/* <Divider sx={{ mb: 2 }} /> */}

            {/* Bill To / Ship To */}
            {/* <Grid container spacing={2} mb={2} className="print-section">
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2">BILL TO:</Typography>
                <Typography variant="body2">{invoiceData?.clientName}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2">SHIP TO:</Typography>
                <Typography variant="body2">---</Typography>
              </Grid>
            </Grid> */}

            {/* <Divider sx={{ mb: 2 }} /> */}

            <Divider sx={{ mb: 2 }} />

            {/* Line Items Table */}
            <Box mb={2} className="print-section">
              <Typography variant="subtitle2">Line Items</Typography>
              <TableContainer className="print-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData?.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item?.unit?.toLocaleString()}</TableCell>
                        <TableCell>{item?.unitPrice?.toLocaleString()}</TableCell>
                        <TableCell>{item?.totalPrice?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Summary & Notes */}
            <Grid container spacing={2} className="print-section">
              <Grid size={{ xs: 8 }}>
                <Typography variant="subtitle2">NOTES:</Typography>
                <Typography variant="body2">
                  Thanks for your business! Please contact us for any questions or concerns.
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box
                  bgcolor="#f0f0f0"
                  p={2}
                  borderRadius={2}
                  sx={{
                    "@media print": {
                      backgroundColor: "#f0f0f0 !important",
                      border: "1px solid #ccc"
                    }
                  }}
                >
                  <Typography variant="subtitle2">GRAND TOTAL</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    Rs. {invoiceData?.totalAmount?.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            {/* Payment Containers Table */}
            <Box mb={2} className="print-section">
              <Typography variant="subtitle2">Payment Containers</Typography>
              <TableContainer className="print-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Container Nos</TableCell>
                      <TableCell>Truck</TableCell>
                      <TableCell>GD</TableCell>
                      <TableCell>Consignee</TableCell>
                      <TableCell>Term</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData?.containers?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.containerNos}</TableCell>
                        <TableCell>{item.truck}</TableCell>
                        <TableCell>{item.gd}</TableCell>
                        <TableCell>{item.cnee}</TableCell>
                        <TableCell>{item.term}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  )
}

export default InvoicePreview
